import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  CircularProgress,
  Icon,
  InputAdornment,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useDebounce } from '@fuse/hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import CheckBox from 'app/componentV4/CheckBox'
import SimpleInput from 'app/componentV4/Input/SimpleInput'
import { PersonSearchIcon } from 'app/components/icons'
import NoGroupPlaceholder from 'app/components/icons/NoGroupPlaceholder'
import {
  HOMELESS_GROUP,
  EVERYONE_GROUP,
  sortGroupByType,
} from 'commons/groupLogic'
import { isEmpty } from 'ramda'

const useStyles = makeStyles((theme) => ({
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '16px',
    width: '100%',
  },
  dropdown: {
    width: '500px',
    minWidth: '320px',
  },
  dialog: {
    width: '100%',
  },
  search: {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.colors['primary-350'],
    },
  },
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
}))

const PAGE_LIMIT = 20

const EmptyGroupsState = ({ search, hasGroup, meta }) => {
  const hasSearch = !isEmpty(search)
  const hasCreatedSomeGroup = meta?.total > 0

  if (hasSearch || !hasGroup) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center py-10 h-full">
        <Typography className="text-center text-blue-950 font-bold text-16">
          <FormattedMessage id="defaultTable.searchnotfound" />
        </Typography>
        <Typography className="mt-1 text-center text-blue-950 text-14">
          <FormattedMessage id="text.adjusting.search" />
        </Typography>
      </div>
    )
  }

  if (!hasCreatedSomeGroup) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center py-10 h-full">
        <Typography className="text-center text-blue-950 font-bold text-16">
          <FormattedMessage id="group.selector.no.group.created" />
        </Typography>
        <Typography className="mt-1 text-center text-blue-950 text-14">
          <FormattedMessage id="group.selector.no.group.created.detail" />
        </Typography>
        <NoGroupPlaceholder className="mt-7" />
      </div>
    )
  }

  return null
}

EmptyGroupsState.propTypes = {
  search: PropTypes.string.isRequired,
  hasGroup: PropTypes.bool.isRequired,
  meta: PropTypes.object,
}

const LoadMoreButton = ({ isLoadmore, groups, meta, loadMore }) => {
  if (meta.currentPage >= meta.lastPage) {
    return null
  }

  if (isLoadmore) {
    return (
      <div className="flex flex-1 justify-center items-center py-4">
        <CircularProgress size={20} />
      </div>
    )
  }

  return (
    <div
      className="flex space-x-2 px-5 py-4 text-center cursor-pointer opacity-50"
      onClick={() =>
        loadMore({
          groups,
          page: meta.currentPage,
          limit: PAGE_LIMIT,
        })
      }
    >
      <Icon className="text-18">refresh</Icon>
      <div className="m-auto">
        <FormattedMessage id="notificationTimeline.loadMore.label" />
      </div>
    </div>
  )
}

LoadMoreButton.propTypes = {
  isLoadmore: PropTypes.bool.isRequired,
  groups: PropTypes.array.isRequired,
  meta: PropTypes.object.isRequired,
  loadMore: PropTypes.func.isRequired,
}

const GroupCheckboxList = ({
  groups,
  selectedTags,
  handleParentCheckboxChange,
  handleTagCheckboxChange,
  checkAllByGroup,
  id,
}) => {
  return (
    <>
      {groups.map((item) => {
        const isHomelessGroup = item.type === HOMELESS_GROUP
        const parentCheckboxId = id
          ? `${id}-parent-${item.id}`
          : `parent-${item.id}`

        if (isHomelessGroup) {
          return (
            <div key={item.id} className="mb-4">
              {item.tags.map((tag) => {
                const tagCheckboxId = id
                  ? `${id}-tag-${tag.id}`
                  : `tag-${tag.id}`
                return (
                  <div key={tag.id} className="flex items-center space-x-2">
                    <CheckBox
                      onChange={() => handleTagCheckboxChange(tag.id)}
                      name={`tag-${tag.id}`}
                      id={tagCheckboxId}
                      checked={selectedTags.includes(tag.id)}
                      inputProps={{
                        'data-testid': id
                          ? `${id}-tag-${tag.id}-checkbox`
                          : `tag-${tag.id}-checkbox`,
                      }}
                    />
                    <Typography
                      component="label"
                      htmlFor={tagCheckboxId}
                      variant="subtitle1"
                      className="text-14 font-bold text-primary cursor-pointer truncate whitespace-nowrap"
                      title={tag.name}
                    >
                      {tag.name}
                    </Typography>
                  </div>
                )
              })}
            </div>
          )
        }

        return (
          <div key={item.id} className="mb-4">
            <div className="flex items-center space-x-2">
              <CheckBox
                onChange={() => handleParentCheckboxChange(item.id)}
                name={`parent-${item.id}`}
                id={parentCheckboxId}
                checked={checkAllByGroup(item.id)}
                inputProps={{
                  'data-testid': id
                    ? `${id}-parent-${item.id}-checkbox`
                    : `parent-${item.id}-checkbox`,
                }}
              />
              <Typography
                component="label"
                htmlFor={parentCheckboxId}
                variant="subtitle1"
                className="text-14 font-bold text-primary cursor-pointer truncate whitespace-nowrap"
                title={item.name}
              >
                {item.name}
              </Typography>
            </div>

            <div className="pl-6">
              {item.tags.map((tag) => {
                const tagCheckboxId = id
                  ? `${id}-tag-${tag.id}`
                  : `tag-${tag.id}`
                return (
                  <div
                    key={tag.id}
                    className="flex items-center ml-5 space-x-2"
                  >
                    <CheckBox
                      onChange={() => handleTagCheckboxChange(tag.id)}
                      name={`tag-${tag.id}`}
                      id={tagCheckboxId}
                      checked={selectedTags.includes(tag.id)}
                      inputProps={{
                        'data-testid': id
                          ? `${id}-tag-${tag.id}-checkbox`
                          : `tag-${tag.id}-checkbox`,
                      }}
                    />
                    <Typography
                      component="label"
                      htmlFor={tagCheckboxId}
                      variant="subtitle1"
                      className="text-14 font-bold text-primary cursor-pointer truncate whitespace-nowrap"
                      title={tag.name}
                    >
                      {tag.name}
                    </Typography>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </>
  )
}

const GroupSelectorContent = ({
  groups,
  selectedTags,
  isLoading,
  meta,
  isLoadmore,
  search: externalSearch,
  onSearchChange,
  handleParentCheckboxChange,
  handleTagCheckboxChange,
  checkAllByGroup,
  loadMore,
  id,
  variant,
  className,
  setSelectedTags,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [search, setSearch] = useState(externalSearch || '')
  const shouldFilterHomelessGroup = variant === 'dialog'

  const groupsForList = useMemo(() => {
    let filteredGroups = groups

    filteredGroups = groups.filter((group) => {
      // Always filter out everyone group
      if (group.type === EVERYONE_GROUP) {
        return false
      }
      // Filter out homeless group when variant is dialog
      if (shouldFilterHomelessGroup && group.type === HOMELESS_GROUP) {
        return false
      }
      return true
    })

    return sortGroupByType(filteredGroups)
  }, [groups, shouldFilterHomelessGroup])

  const hasGroup = useMemo(() => {
    return !isLoading && groups.length > 0 && isEmpty(search)
  }, [isLoading, groups.length, search])

  const onSearchGroup = useDebounce((value) => {
    onSearchChange({
      groups,
      limit: PAGE_LIMIT,
      keyword: value,
    })
  }, 1000)

  return (
    <div
      className={clsx(
        'flex flex-col',
        variant !== 'dropdown' && 'border border-primary-350 rounded-5',
        classes.content,
        variant === 'dropdown' ? classes.dropdown : classes.dialog,
        className,
      )}
    >
      <div className={clsx('px-5 py-5', classes.search)}>
        <SimpleInput
          id={id ? `${id}-searchInput` : 'search-group-in-crm-detail'}
          name="search"
          value={search}
          placeholder={formatMessage({
            id: 'subscriberCrm.editGroup.search.label',
          })}
          size="small"
          fullWidth
          onChange={(event) => {
            const value = event.target.value
            setSearch(value)
            onSearchGroup(value)
          }}
          startAdornment={
            <InputAdornment position="start">
              <PersonSearchIcon
                classes="w-24 text-center"
                color={isEmpty(search) ? '#A8A8A8' : '#183560'}
              />
            </InputAdornment>
          }
        />
      </div>
      {isLoading ? (
        <div
          className={clsx(
            'flex flex-1 justify-center items-center py-10',
            classes.listContainer,
          )}
        >
          <CircularProgress size={24} />
        </div>
      ) : (
        <div className={clsx(classes.listContainer)}>
          {isEmpty(groups) ? (
            <EmptyGroupsState search={search} hasGroup={hasGroup} meta={meta} />
          ) : (
            <>
              <div>
                <GroupCheckboxList
                  groups={groupsForList}
                  selectedTags={selectedTags}
                  handleParentCheckboxChange={handleParentCheckboxChange}
                  handleTagCheckboxChange={handleTagCheckboxChange}
                  checkAllByGroup={checkAllByGroup}
                  id={id}
                />
              </div>
            </>
          )}
          <LoadMoreButton
            isLoadmore={isLoadmore}
            groups={groups}
            meta={meta}
            loadMore={loadMore}
          />
        </div>
      )}
    </div>
  )
}

GroupSelectorContent.propTypes = {
  groups: PropTypes.array.isRequired,
  selectedTags: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  meta: PropTypes.object.isRequired,
  isLoadmore: PropTypes.bool.isRequired,
  search: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  handleParentCheckboxChange: PropTypes.func.isRequired,
  handleTagCheckboxChange: PropTypes.func.isRequired,
  checkAllByGroup: PropTypes.func.isRequired,
  loadMore: PropTypes.func.isRequired,
  id: PropTypes.string,
  variant: PropTypes.oneOf(['dialog', 'dropdown']),
  className: PropTypes.string,
  setSelectedTags: PropTypes.func,
}

GroupSelectorContent.defaultProps = {
  search: '',
  variant: 'dialog',
  setSelectedTags: () => {},
}

export default GroupSelectorContent
